/**
 * Itinerary Chat API endpoints - interactive planning canvas
 */
import { fetchApi, API_BASE_URL } from './apiBase';
import { Itinerary } from '../types';

// === Types ===

export interface ItineraryItem {
  id: string;
  date: string | null;
  time: string | null;
  type: 'activity' | 'meal' | 'transport' | 'accommodation' | 'other';
  name: string;
  location: string;
  address: string;
  duration: string;
  notes: string;
  is_confirmed: boolean;
}

export interface ItineraryData {
  id: number | null;
  title: string | null;
  destination: string | null;
  start_date: string | null;
  end_date: string | null;
  type: 'day_trip' | 'weekend' | 'vacation' | 'excursion';
}

export interface ItineraryChange {
  action: 'added' | 'modified' | 'removed';
  item_id: string;
  name: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  isError?: boolean;
  originalMessage?: string;
  retryCount?: number;
}

export interface ItineraryChatResponse {
  response: string;
  itinerary: ItineraryData;
  items: ItineraryItem[];
  changes: ItineraryChange[];
  suggestions: string[];
  conversation_history: ChatMessage[];
}

export interface TripSuggestion {
  prompt: string;
  label: string;
  tags?: string[];
}

// === Itinerary Chat ===

export async function sendItineraryChatMessage(
  message: string,
  itineraryId?: number,
  model?: string
): Promise<ItineraryChatResponse> {
  const endpoint = itineraryId
    ? `/api/planner/itineraries/${itineraryId}/chat/`
    : '/api/planner/plan/chat/';

  return fetchApi<ItineraryChatResponse>(endpoint, {
    method: 'POST',
    body: JSON.stringify({ message, ...(model && { model }) }),
  });
}

export async function getItineraryChatHistory(itineraryId: number): Promise<{
  conversation_history: ChatMessage[];
  itinerary: ItineraryData;
  items: ItineraryItem[];
}> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/chat/history/`);
}

// === Trip Suggestions ===

export async function getTripSuggestions(limit: number = 3): Promise<{
  suggestions: TripSuggestion[];
}> {
  return fetchApi(`/api/planner/suggestions/?limit=${limit}`);
}

// === Itinerary Item Operations ===

export async function undoItineraryChange(itineraryId: number, expectedRevision: number): Promise<{
  success: boolean;
  message: string;
  itinerary?: ItineraryData;
  items?: ItineraryItem[];
}> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/undo/`, {
    method: 'POST',
    body: JSON.stringify({ expected_revision: expectedRevision }),
  });
}

export async function updateItineraryItemChat(
  itineraryId: number,
  itemId: string,
  updates: Partial<ItineraryItem>,
  expectedRevision: number,
): Promise<{ success: boolean; item: ItineraryItem }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/items/${itemId}/`, {
    method: 'PATCH',
    body: JSON.stringify({ ...updates, expected_revision: expectedRevision }),
  });
}

export async function createItineraryItemChat(
  itineraryId: number,
  item: {
    name: string;
    date?: string;
    time?: string;
    type?: string;
    location?: string;
    address?: string;
    duration?: string;
    notes?: string;
  },
  expectedRevision: number,
): Promise<{ success: boolean; item: ItineraryItem }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/items/`, {
    method: 'POST',
    body: JSON.stringify({ ...item, expected_revision: expectedRevision }),
  });
}

export async function deleteItineraryItemChat(
  itineraryId: number,
  itemId: string | number,
  expectedRevision: number,
): Promise<void> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/items/${itemId}/delete/`, {
    method: 'DELETE',
    body: JSON.stringify({ expected_revision: expectedRevision }),
  });
}

export async function reorderItineraryItemsChat(
  itineraryId: number,
  itemOrder: (string | number)[],
  expectedRevision: number,
): Promise<{ success: boolean }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/items/reorder/`, {
    method: 'POST',
    body: JSON.stringify({ item_order: itemOrder, expected_revision: expectedRevision }),
  });
}

// === Itinerary Sharing ===

export async function duplicateItinerary(
  itineraryId: number,
  newTitle?: string
): Promise<{ success: boolean; itinerary: Itinerary }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/duplicate/`, {
    method: 'POST',
    body: JSON.stringify({ title: newTitle }),
  });
}

/**
 * The plan's share URL.
 *
 * `{ record: false }` asks for the URL without recording a share. Showing a
 * link in an open sheet is not handing it out — the sheet is opened to press
 * "Publish to Explore" as often as to share anything — and since slice 1,
 * the recorded share is what opens a `private` plan to its token. Callers
 * that are about to hand the URL out leave the option off, or call
 * `recordShare` at the act.
 */
export async function getItineraryShareInfo(
  itineraryId: number,
  options?: { record?: boolean }
): Promise<{ share_token: string; share_url: string; title: string }> {
  const query = options?.record === false ? '?record=0' : '';
  return fetchApi(`/api/planner/itineraries/${itineraryId}/share/${query}`);
}

/**
 * The act: this plan's link has just been handed out. Idempotent — the
 * server stamps the first share and a later one does not move it.
 */
export async function recordShare(
  itineraryId: number
): Promise<{ share_token: string; share_url: string; title: string }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/share/record/`, {
    method: 'POST',
  });
}

/**
 * Revoke every link to this plan: the server rotates the token and clears the
 * recorded share in one statement, so a `private` plan closes to every token
 * until its owner shares it again. The new URL comes back unrecorded.
 */
export async function regenerateShareToken(
  itineraryId: number
): Promise<{ share_token: string; share_url: string }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/share/regenerate/`, {
    method: 'POST',
  });
}

export async function getSharedItineraryPublic(
  shareToken: string
): Promise<Itinerary> {
  // This endpoint is public (AllowAny)
  const response = await fetch(`${API_BASE_URL}/api/planner/shared/${shareToken}/`, {
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}
